import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { PackVisual } from "../fx/PackVisual";
import { Button } from "../ui";
import { sfx } from "../../lib/sfx";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

const SPREAD = 9;

function circularDistance(index: number, selected: number, length: number) {
  const raw = Math.abs(index - selected);
  return Math.min(raw, length - raw);
}

/**
 * Step 1 of the ceremony — slow-spinning coverflow wheel. Swipe or use arrows
 * to bring a pack to the front, then lock it in.
 */
export function PackSpinSelector({
  packName,
  packImage,
  packId,
  foilLabel,
  foilColor,
  onConfirm,
  onCancel,
  busy,
}: {
  packName: string;
  packImage: string | null;
  packId: string;
  foilLabel?: string | null;
  foilColor?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const reduced = usePrefersReducedMotion();
  const packImages = useMemo(() => (packImage ? [packImage] : []), [packImage]);
  const autoplay = useRef(Autoplay({ delay: 3200, stopOnInteraction: true, playOnInit: true }));

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center", containScroll: "keepSnaps" },
    reduced ? [] : [autoplay.current],
  );
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || reduced) return;
    if (busy) autoplay.current.stop();
    else autoplay.current.play();
  }, [emblaApi, reduced, busy]);

  function goTo(delta: number) {
    if (busy || !emblaApi) return;
    sfx.tap();
    if (delta < 0) emblaApi.scrollPrev();
    else emblaApi.scrollNext();
  }

  return (
    <div className="flex min-h-[100dvh] w-full flex-1 flex-col items-center justify-center px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex items-center gap-2 text-brand-pink"
      >
        <Sparkles size={16} />
        <p className="font-display text-[10px] font-bold uppercase tracking-[0.3em]">Swipe to pick your pack</p>
      </motion.div>

      <div className="relative mx-auto w-full max-w-lg">
        <div className="overflow-hidden py-4" ref={emblaRef}>
          <div className="flex touch-pan-y">
            {Array.from({ length: SPREAD }).map((_, i) => {
              const d = circularDistance(i, selected, SPREAD);
              const focused = d === 0;
              const scale = focused ? 1 : d === 1 ? 0.82 : 0.64;
              const opacity = focused ? 1 : d === 1 ? 0.55 : 0.25;
              const lift = focused ? -8 : 0;

              return (
                <div key={`slot-${i}`} className="min-w-0 flex-[0_0_72%] px-2 sm:flex-[0_0_62%]">
                  <div
                    className={`mx-auto max-w-[220px] ${reduced ? "" : "transition-[transform,opacity] duration-300 ease-out"}`}
                    style={{
                      transform: `translateY(${lift}px) scale(${scale})`,
                      opacity,
                      zIndex: 10 - d,
                    }}
                  >
                    <div className="rounded-2xl">
                      <PackVisual
                        id={packId}
                        name={packName}
                        images={packImages}
                        foilLabel={foilLabel}
                        foilColor={foilColor}
                        sweep={false}
                        className="aspect-[5/7] w-full shadow-2xl"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          aria-label="Previous pack"
          disabled={busy}
          onClick={() => goTo(-1)}
          className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white/80 backdrop-blur transition-colors hover:bg-black/60 hover:text-white disabled:opacity-40 lg:-left-12"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          aria-label="Next pack"
          disabled={busy}
          onClick={() => goTo(1)}
          className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white/80 backdrop-blur transition-colors hover:bg-black/60 hover:text-white disabled:opacity-40 lg:-right-12"
        >
          <ChevronRight size={20} />
        </button>

        <p className="mt-2 text-center text-[10px] text-white/35">
          Pack {selected + 1} of {SPREAD}
        </p>
      </div>

      <div className="mt-8 flex w-full max-w-sm flex-col items-center gap-2">
        <Button
          className="w-full"
          disabled={busy}
          onClick={() => {
            sfx.tap();
            onConfirm();
          }}
        >
          {busy ? "Sealing your roll…" : "Lock it in"}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-[11px] font-medium text-white/40 transition-colors hover:text-white/70 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
